// src/features/mech/sheetmetal/playbook/SheetmetalPlayBook.jsx
// Sheet Metal-specific PlayBook configuration component (aligned with PlasticsPlayBook structure)

import React, { useEffect } from "react";
import { Box, Typography, Slider } from "@mui/material";
import PerlSlider from "../../../../shared/components/PerlSlider";
import { theme } from "../../../../design/theme";

const { perlColors } = theme.custom;

/**
 * SheetmetalPlayBook - Sheet Metal manufacturing playbook configuration
 *
 * @param {Object} props
 * @param {Object} props.data - PlayBook data object with costParams
 * @param {Function} props.onChange - Callback: (field, value) => void
 * @param {Object} props.costBreakdown - Cost breakdown object with ohp value
 */
export default function SheetmetalPlayBook({ data = {}, onChange, costBreakdown = null }) {
  // Helper function to safely parse numeric values
  const parseNumericValue = (value, defaultValue = 0) => {
    if (value === null || value === undefined || value === "") return defaultValue;
    if (typeof value === "number") return isNaN(value) ? defaultValue : value;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultValue : parsed;
  };

  const costParams = data.costParams || {
    deburrCycleTime: parseNumericValue(data.deburrCycleTime, 0),
    cycleTime: parseNumericValue(data.cycleTime, 0),
    pressTonnage: parseNumericValue(data.pressTonnage || data.machineTonnage, 0),
    ohpRate: parseNumericValue(data.ohpRate, 0),
  };

  const handleParameterChange = (paramName, value) => {
    if (onChange) {
      onChange("costParams", {
        ...costParams,
        [paramName]: value,
      });
    }
  };

  // Calculate OHP percentage for display
  const ohpValue = costBreakdown?.ohp !== undefined && costBreakdown?.ohp !== null
    ? (typeof costBreakdown.ohp === "number" ? costBreakdown.ohp : parseFloat(costBreakdown.ohp))
    : null;
  const totalValue = costBreakdown?.total_should_cost
    ? (typeof costBreakdown.total_should_cost === "number" 
        ? costBreakdown.total_should_cost 
        : parseFloat(costBreakdown.total_should_cost))
    : 0;
  const ohpPercentage = ohpValue !== null && totalValue > 0 
    ? ((ohpValue / totalValue) * 100).toFixed(1) 
    : "0.0";

  // Calculate OHP rate from cost breakdown if available, otherwise use stored value
  // OHP rate = (OHP / (Material + Process)) * 100
  // This is the percentage applied to (Material + Process) to get OHP
  const materialValue = parseNumericValue(costBreakdown?.material, 0);
  const processValue = parseNumericValue(costBreakdown?.process, 0);
  const baseCost = materialValue + processValue;
  const calculatedOhpRate = baseCost > 0 && ohpValue !== null && ohpValue > 0
    ? (ohpValue / baseCost) * 100
    : null;
  
  // Use calculated OHP rate from cost breakdown if available (more accurate)
  // Otherwise fall back to stored ohpRate from playbook
  // This ensures the slider shows the correct value that matches the displayed percentage
  const storedOhpRate = parseNumericValue(costParams.ohpRate, 0);
  const sliderOhpRate = calculatedOhpRate !== null 
    ? calculatedOhpRate 
    : storedOhpRate;

  // Debug logging for OHP rate (moved after all calculations)
  useEffect(() => {
    console.log("🔧 [SheetmetalPlayBook] OHP Rate values:", {
      costParamsOhpRate: costParams.ohpRate,
      dataOhpRate: data.ohpRate,
      dataCostParams: data.costParams,
      parsedValue: parseNumericValue(costParams.ohpRate, 0),
      calculatedOhpRate,
      sliderOhpRate,
      costBreakdown,
      materialValue,
      processValue,
      baseCost,
      ohpValue,
    });
  }, [costParams.ohpRate, data.ohpRate, data.costParams, calculatedOhpRate, sliderOhpRate, costBreakdown, materialValue, processValue, baseCost, ohpValue]);

  const firstRowSliders = [
    {
      key: "deburrCycleTime",
      label: "Deburr Cycle Time",
      value: parseFloat(costParams.deburrCycleTime) || 0,
      min: 0,
      max: 60,
      step: 1,
      unit: " seconds",
      onChange: (e, v) => handleParameterChange("deburrCycleTime", v),
    },
    {
      key: "cycleTime",
      label: "Cycle Time",
      value: parseNumericValue(costParams.cycleTime, 0),
      min: 0,
      max: 120,
      step: 5,
      unit: " seconds",
      onChange: (e, v) => handleParameterChange("cycleTime", v),
    },
    {
      key: "pressTonnage",
      label: "Press Tonnage",
      value: parseInt(costParams.pressTonnage) || 0,
      min: 0,
      max: 500,
      step: 10,
      unit: " tons",
      onChange: (e, v) => handleParameterChange("pressTonnage", v),
    },
  ];

  return (
    <Box>
      <Typography
        variant="subtitle2"
        fontWeight={600}
        sx={{ mb: 2, color: perlColors.textPrimary }}
      >
        Process Parameters
      </Typography>

      <Box
        sx={{
          backgroundColor: perlColors.surface,
          borderRadius: 2,
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: 3,
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          {firstRowSliders.map((slider) => (
            <Box
              key={slider.key}
              sx={{
                minWidth: 220,
                flex: "1 1 280px",
                maxWidth: "calc((100% - 48px) / 3)",
                mb: 2,
              }}
            >
              <PerlSlider
                label={slider.label}
                value={slider.value}
                min={slider.min}
                max={slider.max}
                step={slider.step}
                unit={slider.unit}
                onChange={slider.onChange}
              />
            </Box>
          ))}
        </Box>

        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          <Box
            sx={{
              minWidth: 220,
              flex: "1 1 280px",
              maxWidth: "calc((100% - 48px) / 3)",
              mb: 2,
            }}
          >
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Typography variant="body2" sx={{ color: "text.secondary", flexShrink: 0 }}>
                  OHP Rate:
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    color: "text.primary",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                  }}
                >
                  {ohpValue !== null ? `${ohpPercentage}%` : (costParams.ohpRate ? parseFloat(parseFloat(costParams.ohpRate).toFixed(2)) : 0) + '%'}
                  {ohpValue !== null && (
                    <Typography
                      component="span"
                      sx={{
                        ml: 1,
                        color: perlColors.textSecondary,
                        fontSize: "0.875rem",
                      }}
                    >
                      (of total)
                    </Typography>
                  )}
                </Typography>
              </Box>
              <Slider
                value={Math.max(0, Math.min(30, sliderOhpRate))}
                min={0}
                max={30}
                step={0.1}
                onChange={(e, v) => {
                  // Directly set the OHP rate value - slider controls the rate, not the percentage
                  handleParameterChange("ohpRate", parseFloat(v.toFixed(2)));
                }}
              />
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption">0%</Typography>
                <Typography variant="caption">30%</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
